import { animate, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { getIDType } from '@datorama/akita';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BlendFilterRendererComponent,
  CellTypeRendererComponent,
  DataNoRowOverlayComponent,
  DateClientSideFilterComponent,
  LoadingCellRendererComponent,
} from '@y42/ag-grid';
import { connectState } from '@y42/angular/util';
import { ColumnTypeCellRendererComponent } from '@y42/column-type';
import { ColumnTypeEnum } from '@y42/json-schema';
import {
  ColDef,
  GetContextMenuItemsParams,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  MenuItemDef,
  ProcessCellForExportParams,
  RangeSelectionChangedEvent,
  RowClassParams,
  SideBarDef,
} from 'ag-grid-community';
import cloneDeep from 'lodash/cloneDeep';
import { BehaviorSubject, tap } from 'rxjs';
import { DatasourceColumnUIModel, DatasourceState, DatasourceTableState } from '../../../../state';
import { CardinalityModel } from '../../../cardinality/cardinality.model';
import { HeaderRendererComponent } from '../../../header-renderer';
import { OperationInputAgFilterComponent } from '../../../operation-input';
import { AgGridCssClassEnum } from '../../enums/ag-grid-css-class.enum';
import { ActiveDataCellModel } from '../../models/active-data-cell.model';
import { SelectionRangeStats } from '../../models/selection-range-stats';

const getContextMenuItems = () => ['copy', 'copyWithHeaders', 'export', 'autoSizeAll'];

export enum AgGridAggregationType {
  SUM = 'sum',
  MIN = 'min',
  MAX = 'max',
  COUNT = 'count',
  AVG = 'avg',
  FIRST = 'first',
  LAST = 'last',
}

export const alternativeColorPalette = [
  '#6E6BED',
  '#AC6BED',
  '#AD3B2B',
  '#AD5A2B',
  '#6B9FED',
  '#2BAD8E',
  '#6B9FED',
  '#9797FC',
  '#B1B1DA',
  '#6EE05C',
];

@UntilDestroy()
@Component({
  selector: 'y42-table-data-ag-grid',
  templateUrl: './table-data-ag-grid.component.html',
  styleUrls: ['./table-data-ag-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('displayBox', [
      transition(':enter', [
        style({ height: '0px', opacity: '0' }),
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ height: '*', opacity: '1' })),
      ]),
      transition(':leave', [
        style({ height: '*', opacity: '1' }),
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ height: '0px', opacity: '0' })),
      ]),
    ]),
  ],
})
export class TableDataAgGridComponent implements OnInit, OnChanges {
  constructor(breakpointObserver: BreakpointObserver, private readonly cdRef: ChangeDetectorRef) {
    breakpointObserver
      .observe(['(min-width: 1px) and (max-width: 767px)'])
      .pipe(
        tap((result) => {
          this.isSmallScreen = result.matches;
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  // main ag-grid
  columnDefs: ColDef[] = [];
  gridOptions: GridOptions = {
    rowBuffer: 10,
    suppressAggFuncInHeader: true,
    // groupDisplayType: 'singleColumn',
    // rowSelection: 'multiple',
    // enableFilter: true,
    suppressServerSideInfiniteScroll: false, // TODO: check?
    enableCharts: true,
    enableRangeSelection: true,
    onRangeSelectionChanged: this.onRangeSelectionChanged.bind(this),
    stopEditingWhenCellsLoseFocus: true,
    chartThemes: ['y42theme'], // TODO: add-to-impex ?
    suppressRowDeselection: false,
    headerHeight: 90,
    loadingCellRenderer: 'loadingRenderer',
    noRowsOverlayComponent: 'dataNoRowOverlayComponent',
    noRowsOverlayComponentParams: this.noRowsMessage.bind(this),
    autoGroupColumnDef: {
      // TODO: add-to-impex ?
      headerName: 'Group',
      minWidth: 150,
      width: 130,
      maxWidth: 500,
      sortable: true,
      resizable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
    },
    sideBar: {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressPivotMode: true,
          },
        },
        'filters',
      ],
      hiddenByDefault: false,
    },
    context: {
      componentParent: this,
    },
    defaultColDef: {
      minWidth: 150,
      width: 130,
      maxWidth: 500,
      sortable: true,
      resizable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
      cellStyle: (params) => {
        if (
          params.value === this.activeCell?.cellValue &&
          params.node.id === this.activeCell?.id &&
          params.colDef.headerComponentParams.dataColumn.field_name === this.activeCell?.column.field_name
        ) {
          return { color: '#161926', backgroundColor: '#E9E9F6' };
        }
        return null;
      },
      cellRenderer: 'cellTypeRenderer',
    },
    animateRows: true,
    getContextMenuItems: this.getContextMenuItems.bind(this),
    processCellForClipboard: (params: ProcessCellForExportParams) => {
      if (typeof params.value === 'object') {
        return JSON.stringify(params.value);
      }

      return params.value;
    },
  };

  gridLoading = true;
  public readonly frameworkComponents = {
    loadingRenderer: LoadingCellRendererComponent,
    columnTypeRenderer: ColumnTypeCellRendererComponent,
    dataNoRowOverlayComponent: DataNoRowOverlayComponent,
    dateFilter: DateClientSideFilterComponent,
    blendFilterRenderer: BlendFilterRendererComponent,
    operationInputFilter: OperationInputAgFilterComponent,
    cellTypeRenderer: CellTypeRendererComponent,
  };

  // display detailed chart for each column
  @Input() activeHeaderColumn?: DatasourceColumnUIModel | null;
  @Input() activeCardinality?: CardinalityModel | null;
  @Input() activeCardinalityLoading = false;
  @Input() showAnalyzeColumnBtn = true;
  @Input() totalRows = 0;
  @Input() size = '';
  //
  @Input() showDescription = true;
  @Input() columns: DatasourceColumnUIModel[] = [];
  @Input() cardinality?: CardinalityModel;
  @Input() cardinalityLoading = true;
  @Input() canEditColumn = false;
  @Input() getRowClass?: (params: RowClassParams) => string | string[];

  // frontend vs backend
  @Input() serverSideDatasource?: IServerSideDatasource; // for server side rendenring
  @Input() rowData?: unknown[]; // for frontend renderering

  @Output() modelUpdated: EventEmitter<GridOptions> = new EventEmitter();
  @Output() viewChanged: EventEmitter<void> = new EventEmitter();
  @Output() submitAnalyzeColumn: EventEmitter<string | null> = new EventEmitter();
  @Output() closeFilterPreview: EventEmitter<void> = new EventEmitter();

  // used to update column
  @Input() datasourceId?: getIDType<DatasourceState>;
  @Input() datasourceTableId?: getIDType<DatasourceTableState>;

  // ui
  showTable = true; // weather to show the table or the column view
  isSmallScreen = false;
  activeCell: ActiveDataCellModel | null = null;
  cellLoading = false;
  @Input() verticalButtonLayout?: boolean;

  //size of cashed rows used for export
  @Input() cacheBlockSize?: number;
  @Input() maxBlocksInCache?: number;
  @Input() exportName?: string;
  // footer
  @Input() previewRows?: number;
  @Input() showSize?: boolean;

  // column ag-grid
  columnGridOptions: GridOptions = {
    noRowsOverlayComponent: 'dataNoRowOverlayComponent',
    noRowsOverlayComponentParams: {
      text: 'No Columns to Display',
      body: 'Please apply another filter',
    },
    stopEditingWhenCellsLoseFocus: true,
    defaultColDef: {
      flex: 1,
      minWidth: 150,
      width: 130,
      maxWidth: 500,
      sortable: true,
      resizable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
      filterParams: {
        suppressAndOrCondition: true,
      },
    },
    animateRows: true,
    getContextMenuItems,
    rowClassRules: {
      [AgGridCssClassEnum.Edit](params) {
        return params.data.headerClass === AgGridCssClassEnum.Edit;
      },
      [AgGridCssClassEnum.Source](params) {
        return params.data.headerClass === AgGridCssClassEnum.Source;
      },
    },
  };

  columnColDefs: ColDef[] = [
    {
      headerName: 'Changes',
      field: 'headerClass',
      filter: 'agSetColumnFilter',
      cellRenderer: 'blendFilterRenderer',
      width: 80,
      maxWidth: 80,
      resizable: false,
      hide: this.canEditColumn,
      // valueFormatter: this.filterFormatter
    },
    {
      headerName: 'Type',
      field: 'data_type',
      filter: 'agSetColumnFilter',
      cellRenderer: 'columnTypeRenderer',
      width: 80,
      maxWidth: 80,
      resizable: false,
    },
    {
      headerName: 'Field Name',
      field: 'field_name',
      filter: 'agTextColumnFilter',
      minWidth: 80,
      width: 100,
      cellClass: 'font-medium text-gray-1100',
    },
    {
      headerName: 'Display Name',
      field: 'display_name',
      filter: 'agTextColumnFilter',
      minWidth: 80,
      width: 100,
      editable: this.canEditColumn,
      onCellValueChanged: (params) => this.updateDataColumn(params),
    },
    {
      headerName: 'Description',
      field: 'description',
      filter: 'agNumberColumnFilter',
      minWidth: 80,
      width: 100,
      editable: this.canEditColumn,
      onCellValueChanged: this.updateDataColumn.bind(this),
    },
    {
      headerName: 'Distinct Values',
      field: 'distinct_values',
      filter: 'agN',
      minWidth: 80,
      width: 100,
    },
  ];

  showDataZoom?: boolean;
  rowDataColumns?: DatasourceColumnUIModel[];
  customChartThemes = {
    y42theme: {
      baseTheme: 'ag-pastel',
      palette: {
        fills: alternativeColorPalette,
        strokes: alternativeColorPalette,
      },
      overrides: {
        common: {
          title: {
            fontSize: 18,
            fontFamily: 'Helvetica Neue',
          },
        },
      },
    },
  };

  private readonly cellRangesStats$ = new BehaviorSubject<SelectionRangeStats>({
    sum: null,
    avg: null,
    count: null,
    selected: 0,
  });

  public readonly state = connectState(this, {
    cellRangesStats: this.cellRangesStats$,
  });

  openTogglePanel(type: 'columns' | 'filters') {
    if (!this.gridOptions.api || !this.verticalButtonLayout) {
      return;
    }
    if (this.gridOptions.api.getOpenedToolPanel() === type) {
      this.gridOptions.api.closeToolPanel();
      this.setSideBarVisible(false);
    } else {
      this.setSideBarVisible(true);
      this.gridOptions.api.openToolPanel(type);
    }
  }

  setSideBarVisible(value: boolean) {
    this.gridOptions.api?.setSideBarVisible(value);
  }

  exportData() {
    this.gridOptions.api?.exportDataAsCsv();
  }

  ngOnInit() {
    if (this.verticalButtonLayout) {
      (this.gridOptions.sideBar as SideBarDef).hiddenByDefault = true;
      this.gridOptions.headerHeight = 45;
      this.gridOptions.cacheBlockSize = this.cacheBlockSize;
      this.gridOptions.maxBlocksInCache = this.maxBlocksInCache;
      this.gridOptions.defaultCsvExportParams = { fileName: this.exportName };
    }
    if (this.getRowClass) {
      this.gridOptions.getRowClass = this.getRowClass;
    }
    if (this.serverSideDatasource) {
      this.gridOptions.rowModelType = 'serverSide';
    }
    this.setColumnDefs();
    this.setRowDataColumns();
    this.ensureModifiedColumnVisibility();
  }

  analyzeColumn(field_name: string | null) {
    this.submitAnalyzeColumn.emit(field_name);
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (propName) {
        if (propName === 'cardinality' || propName === 'cardinalityLoading') {
          this.setColumnDefs();
          this.setRowDataColumns();
        }
        if (propName === 'activeHeaderColumn') {
          this.setColumnDefs();
        }
        if (propName === 'activeCardinality') {
          if (this.activeHeaderColumn) {
            this.setColumnDefs();
            // this.showDataZoom =
            //   +(this.activeCardinality?.distinct?.[this.activeHeaderColumn?.field_name || ''] || 0) > 100;
            // this.setUpEcharts();
          }
        }
        if (propName === 'rowData') {
          this.gridOptions?.api?.redrawRows();
        }
        if (propName === 'serverSideDatasource') {
          this.updateServerSideDatasource();
        }
        if (propName === 'columns') {
          this.setRowDataColumns();
        }
        this.cdRef.markForCheck();
      }
    }
  }

  noRowsMessage() {
    return {
      text: 'No Data to Display',
    };
  }

  hideOverlay() {
    if (this.gridOptions.api) {
      this.gridOptions.api.hideOverlay();
    }
  }

  onModelUpdated() {
    const model = this.gridOptions.api?.getModel();
    const processedRows: number = model?.getRowCount() || 0;

    if (this.gridOptions.api) {
      if (processedRows === 0) {
        this.gridOptions.api.showNoRowsOverlay();
      } else {
        this.gridOptions.api.hideOverlay();
      }
    }
    this.modelUpdated.emit(this.gridOptions);
  }

  onGridReady(params: GridReadyEvent) {
    this.gridOptions.api = params.api;
    this.gridOptions?.api.closeToolPanel();
    this.ensureModifiedColumnVisibility();

    this.updateServerSideDatasource();

    this.autoSizeColumns();
    setTimeout(() => {
      this.gridLoading = false;
      this.cdRef.markForCheck();
    }, 100);
  }

  onColumnGridReady(params: GridReadyEvent) {
    if (this.columnGridOptions) {
      this.columnGridOptions.api = params.api;
    }
    setTimeout(() => {
      this.gridLoading = false;
      this.cdRef.markForCheck();
    }, 100);
  }

  // onViewChanged() {
  //   this.viewChanged.emit();
  //   if (!this.showTable) {
  //     this.gridLoading = true;
  //   } else {
  //     if (this.gridOptions?.api) {
  //       this.setColumnDefs();
  //       this.gridOptions.api.refreshHeader();
  //     }
  //   }
  //   this.setActiveCell(null);
  //   this.cdRef.markForCheck();
  // }

  setActiveCell(cellValue: ActiveDataCellModel | null) {
    this.cellLoading = true;
    this.activeCell = cellValue;
    if (this.activeCell) {
      setTimeout(async () => {
        if (
          typeof this.activeCell?.cellValue === 'string' &&
          ((this.activeCell.cellValue.startsWith('{') && this.activeCell.cellValue.endsWith('}')) ||
            (this.activeCell.cellValue.startsWith('[') && this.activeCell.cellValue.endsWith(']')))
        ) {
          let parsed;
          try {
            parsed = JSON.parse(this.activeCell.cellValue);
            this.activeCell.parsedJSON = parsed; // Could be undefined!
          } catch (e) {
            this.activeCell.parsedJSON = cloneDeep(this.activeCell.cellValue);
          }
        } else if (this.activeCell) {
          this.activeCell.parsedJSON = cloneDeep(this.activeCell.cellValue);
        }
        this.cellLoading = false;
        this.cdRef.markForCheck();
      }, 1000);
      if (this.gridOptions?.api) {
        this.gridOptions.api.redrawRows();
      }
    } else {
      if (this.gridOptions?.api) {
        this.gridOptions.api.redrawRows();
      }
    }
    this.cdRef.markForCheck();
  }

  private setRowDataColumns() {
    this.rowDataColumns = cloneDeep(this.columns);
    if (!this.cardinalityLoading) {
      this.rowDataColumns.forEach((col) => {
        col.distinctValues = this.cardinality?.distinct[col.field_name];
        col.distribution = this.cardinality?.top[col.field_name];
      });
    }
  }

  private setColumnDefs() {
    this.columnDefs = this.columns.map((column: DatasourceColumnUIModel) => {
      const isSortable = this.isSortable(column);
      const result: ColDef<DatasourceColumnUIModel> = {
        headerName: column.field_name,
        colId: column.field_name,
        field: column.field_name,
        filter: this.getFilterByType(column),
        sortable: isSortable,
        headerClass: column.headerClass,
        enableRowGroup: true,
        headerComponent: HeaderRendererComponent as new () => HeaderRendererComponent,
        enableValue: true,
        headerComponentParams: {
          dataColumn: column,
          allowBoolEquals: !!this.serverSideDatasource, // not needed in impex-v2
          showAnalyzeColumnBtn: this.showAnalyzeColumnBtn,
          showDescription: this.showDescription, // not needed in impex-v2
          distinct: this.cardinality?.distinct[column.field_name],
          distributions: this.cardinality?.top[column.field_name],
          loading: this.cardinalityLoading, // not needed in impex-v2
          activeHeaderColumn: this.activeHeaderColumn,
          hideHeaderGraph: this.verticalButtonLayout,
          enableSorting: isSortable,
        },
        toolPanelClass: () => `grid-column-type grid-column-type--${column.data_type.toLowerCase()}`,
        aggFunc: AgGridAggregationType.COUNT,
        allowedAggFuncs: this.getAggrOptionsByType(column.data_type),
        cellRendererParams: {
          dataColumn: column,
        },
        // IMPORTANT: valueFormatter is not needed here as we use CellTypeRendererComponent
      };

      if (
        result.filterParams &&
        column.data_type === ColumnTypeEnum.Boolean &&
        this.cardinality?.top[column.field_name]
      ) {
        result.filterParams.values = this.cardinality?.top[column.field_name]?.map((res) => res.value);
      }

      return result;
    });
  }

  private isSortable(column: DatasourceColumnUIModel) {
    const NON_SORTABLE_TYPES: ColumnTypeEnum[] = [
      ColumnTypeEnum.Dynamic,
      ColumnTypeEnum.Bytes,
      ColumnTypeEnum.Array,
      ColumnTypeEnum.Geo,
      ColumnTypeEnum.Struct,
      ColumnTypeEnum.ArrayString,
    ];

    return !NON_SORTABLE_TYPES.includes(column.data_type);
  }

  private getFilterByType(column: DatasourceColumnUIModel) {
    const NON_FILTERABLE_TYPES: ColumnTypeEnum[] = [
      ColumnTypeEnum.Dynamic,
      ColumnTypeEnum.Bytes,
      ColumnTypeEnum.Array,
      ColumnTypeEnum.Geo,
      ColumnTypeEnum.Struct,
      ColumnTypeEnum.ArrayString,
    ];

    if (NON_FILTERABLE_TYPES.includes(column.data_type)) {
      return false;
    }

    if (this.serverSideDatasource) {
      return 'operationInputFilter';
    }

    // only used for non SSR ag-grid
    switch (column.data_type) {
      case ColumnTypeEnum.Numeric:
      case ColumnTypeEnum.Integer:
      case ColumnTypeEnum.Float:
        return 'agNumberColumnFilter';
      case ColumnTypeEnum.String:
        return 'agTextColumnFilter';
      case ColumnTypeEnum.Boolean:
        return 'agSetColumnFilter';
      case ColumnTypeEnum.Date:
      case ColumnTypeEnum.Datetime:
      case ColumnTypeEnum.Timestamp:
        return 'dateFilter';
      default:
        return 'agTextColumnFilter';
    }
  }

  private getAggrOptionsByType(columnType: ColumnTypeEnum) {
    switch (columnType) {
      case ColumnTypeEnum.Numeric:
        return [
          AgGridAggregationType.COUNT,
          AgGridAggregationType.SUM,
          AgGridAggregationType.MIN,
          AgGridAggregationType.MAX,
          AgGridAggregationType.AVG,
        ];
      case ColumnTypeEnum.String:
      case ColumnTypeEnum.Date:
      case ColumnTypeEnum.Datetime:
      case ColumnTypeEnum.Timestamp:
      case ColumnTypeEnum.Boolean:
        return [AgGridAggregationType.COUNT];
      default:
        return [];
    }
  }

  private ensureModifiedColumnVisibility() {
    const modifiedColumns = this.columns.filter((c) => c.headerClass === AgGridCssClassEnum.Edit);
    const firstModifiedColumn = modifiedColumns[0];
    if (firstModifiedColumn && this.gridOptions && this.gridOptions.api) {
      this.gridOptions.api.ensureColumnVisible(firstModifiedColumn.field_name);
    }
  }

  private autoSizeColumns() {
    const colIds = this.gridOptions?.columnApi?.getAllDisplayedColumns().map((col) => col.getColId()) || [];
    this.gridOptions?.columnApi?.autoSizeColumns(colIds);
    setTimeout(() => {
      // this will make the execution after the above boolean has changed
      this.gridLoading = false;
      this.cdRef.markForCheck();
    }, 200);
  }

  private getContextMenuItems(params: GetContextMenuItemsParams) {
    const contextMenuItems: Array<string | MenuItemDef> = ['copy', 'autoSizeAll', 'export', 'chartRange'];

    if (this.cellRangesStats$.value.selected > 0) {
      contextMenuItems.push({
        name: 'Clear selection',
        action: (() => {
          this.gridOptions?.api?.clearRangeSelection();
        }).bind(this),
        cssClasses: ['redFont', 'bold'],
      });
    }

    if (!params?.node?.group) {
      contextMenuItems.push({
        name: 'Analyze Value as JSON',
        action: (() => {
          this.setActiveCell({
            cellValue: params.value,
            id: params.node?.id || '',
            column: params.column?.getColDef().headerComponentParams.dataColumn,
            parsedJSON: null,
          });
        }).bind(this),
        cssClasses: ['redFont', 'bold'],
        /*icon: '<img src="../images/skills/windows.png"/>',*/
      });
    }
    return contextMenuItems;
  }

  private onRangeSelectionChanged(__: RangeSelectionChangedEvent) {
    const api = this.gridOptions?.api;
    const cellRanges = api?.getCellRanges();

    if (!cellRanges || cellRanges.length === 0) {
      this.cellRangesStats$.next({
        avg: null,
        sum: null,
        count: null,
        selected: 0,
      });
      return;
    }

    let sum = 0;
    let count = 0;
    let selected = 0;

    cellRanges.forEach((range) => {
      if (range.startRow?.rowIndex !== undefined && range.endRow?.rowIndex !== undefined) {
        // get starting and ending row, remember rowEnd could be before rowStart
        const startRow = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
        const endRow = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);

        for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
          range.columns.forEach((column) => {
            const rowModel = api?.getModel();
            const rowNode = rowModel?.getRow(rowIndex);
            if (rowNode) {
              const value = api?.getValue(column, rowNode);
              if (typeof value === 'number') {
                sum += value;
                count += 1;
              }
              selected += 1;
            }
          });
        }
      }
    });

    const avg = count !== 0 ? sum / count : 0;
    this.cellRangesStats$.next({
      avg: count > 0 ? avg : null,
      sum: count > 0 ? sum : null,
      count: count > 0 ? count : null,
      selected,
    });
  }

  private updateDataColumn(params: { data: { id: number }; newValue: string; colDef: { field?: string } }) {
    const _data = {
      [params.colDef.field!]: params.newValue,
    };
    // TODO: ???
    // this.tableColumnsService
    //   .updateRequest(
    //     `${params.data.id}`,
    //     data,
    //     false,
    //     false,
    //     this.datasourceId as string, // TODO: type...
    //     this.datasourceTableId as string, // TODO: type...
    //   )
    //   .pipe(untilDestroyed(this))
    //   .subscribe();
  }

  private updateServerSideDatasource() {
    if (this.serverSideDatasource && this.gridOptions?.api) {
      this.gridOptions?.api.setServerSideDatasource(this.serverSideDatasource);
    }
  }
}
